import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { SEOHead } from "@/components/SEOHead";

const STAGES = ["pending", "confirmed", "preparing", "out_for_delivery", "delivered"];
const LABELS = {
  pending: "Order received",
  confirmed: "Confirmed",
  preparing: "In the oven",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderId, setOrderId] = useState(params.get("id") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setOrder(null);
    try {
      const r = await api.get(`/orders/track/${orderId}`, { params: { email } });
      setOrder(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (params.get("id") && params.get("email")) {
      lookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stageIdx = order ? STAGES.indexOf(order.status) : -1;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-20" data-testid="track-page">
      <SEOHead title="Track Your Order | Cocoamaya Patisserie" noindex />
      <p className="eyebrow mb-3">Order Tracking</p>
      <h1 className="font-display text-5xl tracking-tight mb-10">Where's my order?</h1>

      <form onSubmit={lookup} className="grid md:grid-cols-3 gap-3 mb-12">
        <input className="input-field md:col-span-2" placeholder="Order ID" value={orderId} onChange={(e) => setOrderId(e.target.value)} required data-testid="track-id-input" />
        <input className="input-field" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="track-email-input" />
        <button className="btn-primary md:col-span-3 justify-center" disabled={loading} data-testid="track-lookup-btn">
          {loading ? "Looking up…" : "Track order"}
        </button>
      </form>

      {order && (
        <div className="border p-8" style={{ borderColor: "var(--line)" }} data-testid="track-result">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div>
              <p className="eyebrow mb-2">Order</p>
              <p className="font-mono text-sm">{order.id}</p>
            </div>
            <div className="text-right">
              <p className="eyebrow mb-2">Total</p>
              <p className="font-display text-2xl">₹{Math.round(order.total)}</p>
            </div>
          </div>

          {order.status === "cancelled" ? (
            <div className="mt-10 p-6 text-center" style={{ background: "var(--bg-soft)" }}>
              <p className="font-display text-2xl">{LABELS.cancelled}</p>
            </div>
          ) : (
            <div className="mt-12">
              <div className="relative">
                <div className="absolute top-3 left-0 right-0 h-px" style={{ background: "var(--line)" }} />
                <div
                  className="absolute top-3 left-0 h-px transition-all duration-700"
                  style={{ background: "var(--accent)", width: `${(Math.max(stageIdx, 0) / (STAGES.length - 1)) * 100}%` }}
                />
                <div className="relative flex justify-between">
                  {STAGES.map((s, i) => (
                    <div key={s} className="flex flex-col items-center" style={{ width: `${100 / STAGES.length}%` }}>
                      <div
                        className="w-6 h-6 rounded-full border-2 z-10"
                        style={{
                          background: i <= stageIdx ? "var(--accent)" : "var(--bg)",
                          borderColor: i <= stageIdx ? "var(--accent)" : "var(--line)",
                        }}
                      />
                      <p className="text-xs mt-3 text-center" style={{ color: i <= stageIdx ? "var(--ink)" : "var(--ink-soft)" }}>
                        {LABELS[s]}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 pt-8 border-t" style={{ borderColor: "var(--line)" }}>
            <p className="eyebrow mb-3">Items</p>
            <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
              {order.items.map((i) => (
                <li key={i.product_id} className="flex justify-between py-3 text-sm">
                  <span>{i.name} ×{i.quantity}</span>
                  <span>₹{Math.round(i.price * i.quantity)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
